import React, { useState } from 'react';
import {
  ButtonTertiary,
  cssColorValue,
  desktopMestoBold,
  IconChevronDown,
  IconChevronUp,
  NavBar,
  NavBarList,
  screenMdMin,
  semanticColors,
  spacer32,
  spacer8,
  TextLink,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { HeaderSchema, LinkList } from 'types/navigation';
import { useTranslation } from 'providers/translations/TranslationProvider';

import useDropdown, { DropdownContext } from './hooks/useDropdown';
import { NavBarPopover } from './Popover';
import { isMastheadLinkList, PopoverLink } from './types';

const StyledNavBar = styled(NavBar)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @media (max-width: ${screenMdMin}) {
    display: none;
  }
`;

const StyledNavBarList = styled(NavBarList)`
  gap: ${spacer32};
  line-height: 24px;
  margin: 0;
  overflow-x: visible;
  padding: 0;
  ${desktopMestoBold};
`;

const NavLink = styled(TextLink)`
  cursor: pointer;
  ${desktopMestoBold};

  && {
    text-decoration: none;

    &:active,
    &:hover,
    &:focus {
      text-decoration: none;
    }

    &:focus {
      color: ${cssColorValue(semanticColors.textBrightAccent)};
    }
  }
`;

const NavBarPopoverButton = styled(ButtonTertiary)`
  color: ${cssColorValue(semanticColors.textBase)};
  cursor: pointer;
  padding: 0;
  min-block-size: auto;
  ${desktopMestoBold};

  && {
    text-decoration: none;

    &:active,
    &:hover,
    &:focus {
      transform: none;
    }

    &:hover,
    &:focus {
      color: ${cssColorValue(semanticColors.textBrightAccent)};
    }

    &::after {
      inline-size: 100%;
      bottom: -3px;
    }

    & > * {
      margin-inline-start: ${spacer8};
    }
  }
`;

const StyledIconChevronDown = styled(IconChevronDown)`
  transform: translateY(1px);
`;

const StyledIconChevronUp = styled(IconChevronUp)`
  transform: translateY(3px);
`;

const NavbarDropdown = ({ linkListEntry, headerEntry }: { linkListEntry: LinkList; headerEntry: HeaderSchema }) => {
  const { handlers, open } = useDropdown(linkListEntry.label);
  const { t } = useTranslation();
  const links = (linkListEntry.entries || []).map<PopoverLink>(linkListItem => {
    const link = linkListItem.link;
    return {
      href: link.href,
      linkText: t(link.title),
      level: linkListItem?.nestingLevel,
      target: link.target,
    };
  });

  return (
    <NavBarPopover
      isOpen={open}
      links={links}
      onMouseEnter={handlers.handleOnMouseEnter}
      onMouseLeave={handlers.handleOnMouseLeave}
    >
      <NavBarPopoverButton
        href=""
        iconTrailing={() =>
          open ? (
            <StyledIconChevronUp size="small" aria-label={t(headerEntry.dropdownMenuLabelClose)} />
          ) : (
            <StyledIconChevronDown size="small" aria-label={t(headerEntry.dropdownMenuLabelOpen)} />
          )
        }
        onKeyDown={handlers.handleOnKeyDown}
        onMouseDown={handlers.handleOnMouseDown}
      >
        {t(linkListEntry.label)}
      </NavBarPopoverButton>
    </NavBarPopover>
  );
};

type Props = {
  headerEntry: HeaderSchema;
};

export const NavBarLinks = ({ headerEntry }: Props) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const { t } = useTranslation();

  return (
    <StyledNavBar
      list={
        <nav dir="ltr">
          <StyledNavBarList>
            <DropdownContext.Provider value={{ activeDropdown, setActiveDropdown }}>
              {headerEntry.centerNav.map((item, index) => {
                if (isMastheadLinkList(item)) {
                  return (
                    <li key={item.label + index}>
                      <NavbarDropdown linkListEntry={item} headerEntry={headerEntry} />
                    </li>
                  );
                }
                return (
                  <li key={index}>
                    <NavLink href={item.href} target={item.target}>
                      {t(item.title)}
                    </NavLink>
                  </li>
                );
              })}
            </DropdownContext.Provider>
          </StyledNavBarList>
        </nav>
      }
    />
  );
};
